// web/src/hooks/use-auth.tsx
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, setTokens, clearTokens, getAccessToken } from '@/lib/api'
import type { User, AuthResponse } from '@/lib/types'
import { track, identifyUser, getAttribution } from '@/lib/analytics'

interface AuthCtx {
  user: User | null
  token: string | null
  login: (email: string, password: string) => Promise<string | null>
  signup: (email: string, password: string, name: string, tenant?: string, inviteCode?: string, claim?: string) => Promise<string | null>
  logout: () => void
  loading: boolean
}

const AuthContext = createContext<AuthCtx | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // On mount, attempt to restore session via the HttpOnly refresh cookie
    fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    })
      .then(async res => {
        if (res.ok) {
          const data: AuthResponse = await res.json()
          setTokens(data.access_token)
          setToken(data.access_token)
          // /api/auth/me returns { user, tenant } — unwrap to the user, the
          // same shape login/signup put through setUser. Without this the whole
          // wrapper object lands in state and user.name is undefined (avatar "?").
          return api.get<{ user: User }>('/api/auth/me').then(r => r.user)
        }
        return null
      })
      .then(userData => {
        if (userData) {
          setUser(userData)
          identifyUser(userData.id, { email: userData.email })
        }
      })
      .catch(() => {
        clearTokens()
        setToken(null)
      })
      .finally(() => setLoading(false))
  }, [])

  const login = async (email: string, password: string): Promise<string | null> => {
    const res = await api.post<AuthResponse & { redirect_url?: string }>('/api/auth/login', { email, password })
    if (res.redirect_url) {
      return res.redirect_url
    }
    setTokens(res.access_token)
    setToken(res.access_token)
    setUser(res.user)
    identifyUser(res.user.id, { email: res.user.email })
    return null
  }

  const signup = async (email: string, password: string, name: string, tenant?: string, inviteCode?: string, claim?: string): Promise<string | null> => {
    track('signup_started')
    const url = inviteCode ? `/api/auth/signup?invite=${encodeURIComponent(inviteCode)}` : '/api/auth/signup'
    const attr = getAttribution()
    const res = await api.post<AuthResponse & { redirect_url?: string }>(url, { email, password, name, tenant, ...attr, claim })
    if (res.redirect_url) {
      return res.redirect_url
    }
    setTokens(res.access_token)
    setToken(res.access_token)
    setUser(res.user)
    identifyUser(res.user.id, { email: res.user.email })
    return null
  }

  const logout = () => {
    clearTokens()
    setToken(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, token, login, signup, logout, loading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
